import React, { useContext, useEffect } from "react";
import { usePaginationFragment } from "react-relay";

import cx from "classnames";
import { graphql } from "relay-runtime";

import type { TeamType, UserType } from "~/common/AssigneeFilter";
import ViewportContext from "~/common/ViewportContext";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import Objective, { type ObjectiveType } from "~/okrs/Objective";
import { OKRsContext } from "~/okrs/ObjectiveList";
import Avatar from "~/ui/legacy/Avatar";

import BigCreateObjectiveButton from "./components/BigCreateObjectiveButton";
import EmptyStateArchived from "./components/EmptyStateArchived";

import type { ObjectivePaginationQuery } from "~/__generated__/ObjectivePaginationQuery.graphql";
import type { ObjectiveQueryQuery } from "~/__generated__/ObjectiveQueryQuery.graphql";
import type { ObjectiveQueryViewer$key } from "~/__generated__/ObjectiveQueryViewer.graphql";

import styles from "~/okrs/ObjectiveList.module.css";

const objectivesQuery = graphql`
	query ObjectiveQueryQuery(
		$parent: ID
		$node: ID
		$display: DisplayType
		$title: String
		$status: ObjectiveStatus
		$archived: Boolean
		$assignedTo: [ID!]
		$cycles: [ID!]
	) {
		viewer {
			...ObjectiveQueryViewer
		}
	}
`;

const objectivePaginationQuery = graphql`
	fragment ObjectiveQueryViewer on Viewer
	@argumentDefinitions(
		before: { type: "String" }
		after: { type: "String" }
		first: { type: "Int" }
		last: { type: "Int", defaultValue: 50 }
	)
	@refetchable(queryName: "ObjectivePaginationQuery") {
		objectives(
			parent: $parent
			node: $node
			display: $display
			title: $title
			status: $status
			archived: $archived
			assignedTo: $assignedTo
			cycles: $cycles
			before: $before
			after: $after
			first: $first
			last: $last
		) @connection(key: "ObjectiveQuery_objectives") {
			edges {
				node {
					...ObjectiveQuery_Info @relay(mask: false)
					children {
						...ObjectiveQuery_Info @relay(mask: false)
					}
				}
			}
		}
	}
`;

graphql`
	fragment ObjectiveQuery_Info on Objective {
		id
		title
		visibility
		dueDate
		assigneeUser {
			id
			email
			image
			fullName
			isGuest
		}
		assigneeTeam {
			id
			title
		}
		contributors {
			id
			email
			image
			fullName
			isGuest
		}
		contributorsTeams {
			id
			title
		}
		description
		status
		archived
		weight
		visibility
		cycle {
			id
		}
		parent {
			id
			title
			level
			cycle {
				startDate
				endDate
			}
		}
		progress

		keyResults {
			id
			title
			dueDate
			assigneeUser {
				id
				email
				image
				fullName
				isGuest
			}
			assigneeTeam {
				id
				title
			}
			metricProgress
			metricType
			metricCurrency
			metricStart
			metricCurrent
			metricTarget
			weight
			order
			progress
		}
		hasAccess
	}
`;

type ObjectiveQueryProps = {
	parent?: string; // Passing parent will query all child nodes for a particular parent
	node?: string; // Passing node will query only one particular node
	archived?: boolean;
	alwaysIncludeCreateButton?: boolean;
};

export default function ObjectiveQuery({ parent, node, archived, alwaysIncludeCreateButton }: ObjectiveQueryProps) {
	const { fetchOptions, filters, resetFetchPolicy } = useContext(OKRsContext);
	const { isMobile } = useContext(ViewportContext);

	const props = useLazyLoadQuery<ObjectiveQueryQuery>(
		objectivesQuery,
		{
			parent,
			node,
			archived,
			display: filters?.view,
			title: filters?.filterName,
			status: filters?.filterStatus,
			assignedTo:
				filters?.initialFilterAssignees && filters?.initialFilterAssignees.length > 0
					? filters?.initialFilterAssignees
					: filters?.filterAssignee?.map(a => a.user?.id || a.team?.id || ""),
			cycles: filters?.filterCycles || [],
		},
		fetchOptions,
	);

	const { data: viewer } = usePaginationFragment<ObjectivePaginationQuery, ObjectiveQueryViewer$key>(
		objectivePaginationQuery,
		props.viewer,
	);

	// There are scenarios that we want to force relay to fetch from the network
	// But right after we finish fetching we want to rollback to store-and-network
	useEffect(() => {
		if (fetchOptions?.fetchPolicy === "network-only") {
			resetFetchPolicy?.();
		}
	}, [props, fetchOptions, resetFetchPolicy]);

	if (!viewer.objectives?.edges) {
		return null;
	}

	if (viewer.objectives.edges.length === 0 && !parent) {
		return archived ? <EmptyStateArchived /> : <BigCreateObjectiveButton includeEmptyState={true} />;
	}

	if (filters?.view === "TREE") {
		return (
			<div
				className={cx(styles.listItems, {
					[styles.isChild]: !!parent,
				})}
			>
				{viewer.objectives.edges.map(objective => (
					// @ts-ignore
					<Objective key={objective.node?.id} {...objective.node} />
				))}
			</div>
		);
	}

	// Group objectives by assignee
	const assigneeGroups: {
		[key: string]: { assigneeUser?: UserType; assigneeTeam?: TeamType; objectives: ObjectiveType[] };
	} = {};
	viewer.objectives.edges.forEach(objective => {
		if (!objective) {
			return;
		}

		const assigneeId = objective.node.assigneeUser?.id || objective.node.assigneeTeam?.id;
		if (!assigneeId) {
			return;
		}

		if (assigneeGroups[assigneeId]) {
			assigneeGroups[assigneeId].objectives.push(objective.node);
			return;
		}

		assigneeGroups[assigneeId] = {
			assigneeUser: objective?.node.assigneeUser || undefined,
			assigneeTeam: objective?.node.assigneeTeam || undefined,
			objectives: [objective.node],
		};
	});

	return (
		<div className={styles.list}>
			{!isMobile && (
				<div className={styles.listHeader}>
					<div className={styles.title}>Objective</div>
					<div className={styles.dueDate}>Due Date</div>
					<div className={styles.assignedTo}>Assigned to</div>
					<div className={styles.status}>Status</div>
					<div className={styles.progress}>Progress</div>
					<div className={styles.goToParent} />
				</div>
			)}

			<div className={styles.listItems}>
				{Object.keys(assigneeGroups).map(assigneeId => {
					const assigneeUser = assigneeGroups[assigneeId].assigneeUser;
					const assigneeTeam = assigneeGroups[assigneeId].assigneeTeam;

					return (
						<div key={assigneeId} className={styles.assigneeGroup}>
							{assigneeUser && (
								<div className={styles.assigneeData}>
									<Avatar link={false} user={assigneeUser} width="1.5rem" />
									<span>{assigneeUser.fullName}</span>
								</div>
							)}

							{assigneeTeam && (
								<div className={styles.assigneeData}>
									<Avatar
										link={false}
										team={assigneeTeam}
										width="1.5rem"
										uiAvatarOptions={{ textForColor: assigneeTeam.title }}
									/>
									<span>{assigneeTeam.title}</span>
								</div>
							)}

							<div className={styles.listItems}>
								{assigneeGroups[assigneeId].objectives.map(objective => (
									<Objective key={objective?.id} {...objective} />
								))}
							</div>
						</div>
					);
				})}
			</div>
			{alwaysIncludeCreateButton && <BigCreateObjectiveButton />}
		</div>
	);
}
